import classNames from "classnames";
import Clickable from "components/Clickable";
import { ButtonPrimary } from "components/Clickable/Buttons";
import { Tab, TabPanel, Tabs } from "components/Tabs";
import { Tooltip } from "components/Tooltip/Tooltip";
import { getSelectedFinishImagePath } from "components/VisualConfigurator";
import {
    IVisualizerPanelOptionProps,
    IVisualizerPanelRulesProps,
    IVisualizerStepProps,
} from "components/Visualizer";
import { useRouterParams } from "components/useRouterParams";
import { startsWithVowel, usePreviousValue } from "helpers/utils";
import Link, { LinkProps } from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { Pressable, VisuallyHidden } from "react-aria";
import { Button } from "react-aria-components";
import { CloudinaryImage } from "react-tiny-cloudinary";

import CaretIcon from "../../public/svg/caret.svg";
import EmetekSelectIcon from "../../public/svg/emtek-select-logo.svg";
import XIcon from "../../public/svg/x.svg";

import styles from "./attribute-panel-multiattribute.module.scss";

type AttributePanelMultiAttributeProps = {
    rule: IVisualizerPanelRulesProps;
    ruleArray: Array<IVisualizerPanelRulesProps>;
    currentStep: IVisualizerStepProps;
    nextStep: IVisualizerStepProps | null;
    selectedOption: IVisualizerPanelOptionProps | undefined;
    selectedFilter: IVisualizerPanelOptionProps | undefined;
    invalidOption: string;
    onInvalidOption: (option: string) => void;
    onNextStep: () => void;
};

type OptionTabsProps = {
    optionTypes: Array<string | null>;
    onOptionSelect: (option: IVisualizerPanelOptionProps | undefined) => void;
    calculateOptionSelectionUrl: (
        option: IVisualizerPanelOptionProps,
    ) => LinkProps["href"];
    currentRule: IVisualizerPanelRulesProps | undefined;
    currentFilter: IVisualizerPanelOptionProps | undefined;
    selectedOption: IVisualizerPanelOptionProps | undefined;
};

type OptionAccordionProps = {
    theme: string | null;
    options: Array<IVisualizerPanelOptionProps> | undefined;
    onOptionSelect: (option: IVisualizerPanelOptionProps) => void;
    calculateOptionSelectionUrl: (
        option: IVisualizerPanelOptionProps,
    ) => LinkProps["href"];
    currentFilter: IVisualizerPanelOptionProps | undefined;
    currentRuleID: number;
};

type StepButtonProps = {
    ruleArray: Array<IVisualizerPanelRulesProps>;
    currentRuleIndex: number;
    nextStep: IVisualizerStepProps | null;
    onNextStep: () => void;
    onNewRuleSelected: (newRule: IVisualizerPanelRulesProps) => void;
    nextStepValid: boolean;
};

function createPrefetcher(href: LinkProps["href"]) {
    if (
        !(
            window.__NEXT_DATA__.buildId &&
            window.__NEXT_DATA__.buildId !== "development"
        )
    ) {
        // If we're running in development or can't find nextjs build information, bail
        return;
    }
    if (typeof href === "string" || !("query" in href)) {
        // This was created for a specific type of circumstance, bail if things look unfamiliar
        return;
    }

    const query = new URLSearchParams(Object.entries(href.query as any));

    return () => {
        fetch(
            `/_next/data/${
                window.__NEXT_DATA__.buildId
            }/en/${window.location.pathname.replaceAll(
                /(^\/|\/$)/g,
                "",
            )}.json?${query}`,
            { priority: "low" } as any,
        ).catch(() => {
            // Ignore errors to avoid blowing up sentry
        });
    };
}

const OptionAccordion = ({
    theme,
    options,
    onOptionSelect,
    calculateOptionSelectionUrl,
    currentFilter,
    currentRuleID,
}: OptionAccordionProps) => {
    const [showOptions, setShowOptions] = useState(true);
    const routerParams = useRouterParams({
        method: "push",
        shallow: false,
        scroll: false,
    });

    useEffect(() => {
        return () => {
            // Return accordion to collapsed state on unmount
            setShowOptions(false);
        };
    }, []);

    const optionsOfCurrentTheme = options
        ? options.filter((o) => {
              if (o.themes) {
                  return o.themes.find((t) => t.name === theme);
              }

              return false;
          })
        : null;

    const allOptions = optionsOfCurrentTheme
        ? optionsOfCurrentTheme.map((option, i) => {
              let showOption = true;
              let selectedFinishImagePath = undefined;

              if (currentFilter) {
                  const finishPath = getSelectedFinishImagePath(
                      [currentFilter],
                      option.finish_images,
                  );

                  if (finishPath) {
                      selectedFinishImagePath = finishPath;
                  } else if (finishPath === "") {
                      selectedFinishImagePath = option.primary_image?.image;
                  } else {
                      showOption = false;
                  }
              } else if (option.primary_image?.image) {
                  selectedFinishImagePath = option.primary_image?.image;
              }

              // If there are no options that match the current filter, hide this accordion
              if (!showOption) {
                  return null;
              }

              const url = calculateOptionSelectionUrl(option);

              return (
                  <Link
                      key={`${theme}--${option.option}--${i}`}
                      href={url}
                      passHref
                  >
                      <Clickable
                          unstyled
                          onClick={() => onOptionSelect(option)}
                          onMouseEnter={createPrefetcher(url)}
                          className={classNames({
                              [styles.optionContainer]: true,
                              [styles.optionContainerSelected]:
                                  option.id.toString() ===
                                  routerParams.getParamValue(
                                      `rule-${currentRuleID}`,
                                  ),
                          })}
                      >
                          <CloudinaryImage
                              type={
                                  option.primary_image?.image
                                      ? "fetch"
                                      : "upload"
                              }
                              cldId={
                                  (option.finish_images &&
                                  selectedFinishImagePath
                                      ? selectedFinishImagePath
                                      : option.primary_image?.image) ??
                                  undefined
                              }
                              cldSrcSet={[130]}
                              aspectRatio="1:1"
                          >
                              <img alt="" sizes="130px" />
                          </CloudinaryImage>
                          <p>{option.option}</p>
                      </Clickable>
                  </Link>
              );
          })
        : [];

    if (allOptions.every((el) => el === null)) {
        return <></>;
    }

    return (
        <section className={styles.accordion}>
            <div
                className={classNames({
                    [styles.accordionButton]: true,
                    [styles.accordionCollapsed]: !showOptions,
                })}
                onClick={() => {
                    setShowOptions((prevShow) => !prevShow);
                }}
            >
                <span>{theme ? theme : "Other"}</span>
                <CaretIcon />
            </div>
            <div
                className={classNames({
                    [styles.accordionOption]: true,
                    [styles.accordionOptionHide]: !showOptions,
                })}
            >
                {allOptions}
            </div>
        </section>
    );
};

export const OptionTabs = ({
    optionTypes,
    onOptionSelect,
    calculateOptionSelectionUrl,
    currentRule,
    currentFilter,
    selectedOption,
}: OptionTabsProps) => {
    if (!currentRule) return null;

    return (
        <Tabs
            className={styles.tabs}
            tabs={
                <>
                    {optionTypes.map((type: string | null, i) => {
                        if (type === null) {
                            return null;
                        }

                        return (
                            <Tab
                                key={`${type}--${i}`}
                                className={styles.tabTitle}
                                id={type}
                            >
                                {type}
                                {type === "Emtek Select" && (
                                    <EmetekSelectIcon
                                        className={styles.emtekSelectIcon}
                                    />
                                )}
                            </Tab>
                        );
                    })}
                </>
            }
            defaultSelectedKey={selectedOption?.obj_type.display_name}
            panels={
                <>
                    {optionTypes.map((type: string | null, i) => {
                        if (type === null) {
                            return null;
                        }

                        const thisTypesOptions = currentRule.options
                            ? currentRule.options.filter(
                                  (option: IVisualizerPanelOptionProps) => {
                                      return (
                                          option.obj_type.display_name === type
                                      );
                                  },
                              )
                            : [];

                        const uniqueOptionThemes = [
                            ...new Set(
                                thisTypesOptions
                                    .map((o) => {
                                        if (o.themes) {
                                            return o.themes
                                                .map((t) => t.name)
                                                .flat();
                                        }

                                        return null;
                                    })
                                    .flat(),
                            ),
                        ].sort();

                        return (
                            <TabPanel
                                id={type}
                                key={`TP-${currentFilter?.option}--${i}`}
                            >
                                {type === "Emtek Select" && (
                                    <p className={styles.emtekSelectCopy}>
                                        The Emtek Select Program offers a new
                                        level of customization: choose your
                                        textured lever grip, stem design, and
                                        pair with any of our available rosettes
                                        options – in any mix of finishes – for
                                        hundreds of unique combinations, made
                                        just for you and your home project.
                                    </p>
                                )}
                                {uniqueOptionThemes.map(
                                    (style: string | null, j) => {
                                        return (
                                            <OptionAccordion
                                                key={`OA-${currentFilter?.option}--${i}-${j}`}
                                                theme={style}
                                                options={thisTypesOptions}
                                                onOptionSelect={onOptionSelect}
                                                calculateOptionSelectionUrl={
                                                    calculateOptionSelectionUrl
                                                }
                                                currentRuleID={currentRule.id}
                                                currentFilter={currentFilter}
                                            />
                                        );
                                    },
                                )}
                            </TabPanel>
                        );
                    })}
                </>
            }
        />
    );
};

export const StepButtons = ({
    ruleArray,
    currentRuleIndex,
    nextStep,
    onNextStep,
    onNewRuleSelected,
    nextStepValid,
}: StepButtonProps) => {
    const progressionTitle = nextStep ? nextStep.title : "Next: Summary";
    const nextRule = ruleArray[currentRuleIndex + 1]
        ? ruleArray[currentRuleIndex + 1]
        : null;

    return (
        <div className={styles.stepButtons}>
            {currentRuleIndex !== 0 && (
                <ButtonPrimary
                    className={styles.prevStepButton}
                    onClick={() => {
                        onNewRuleSelected(ruleArray[currentRuleIndex - 1]);
                    }}
                >
                    <CaretIcon />
                    {ruleArray[currentRuleIndex - 1]?.title}
                </ButtonPrimary>
            )}
            {nextRule && (
                <ButtonPrimary
                    className={styles.stepButton}
                    onClick={() => {
                        if (nextRule) {
                            onNewRuleSelected(nextRule);
                        }
                    }}
                >
                    {`${nextRule.title}`}
                    <CaretIcon />
                </ButtonPrimary>
            )}
            {nextStepValid && !nextRule && (
                <ButtonPrimary
                    className={styles.stepButton}
                    onClick={onNextStep}
                >
                    {progressionTitle}
                    <CaretIcon />
                </ButtonPrimary>
            )}
        </div>
    );
};

export const AttributePanelMultiAttribute = ({
    rule,
    ruleArray,
    currentStep,
    nextStep,
    onNextStep,
    selectedOption,
    selectedFilter,
    invalidOption,
    onInvalidOption,
}: AttributePanelMultiAttributeProps) => {
    const router = useRouter();
    const [currentRuleIndex, setCurrentRuleIndex] = useState(-1);
    const [nextStepValid, setNextStepValid] = useState(false);
    const [lastSelectedFilter, setLastSelectedFilter] = useState<
        IVisualizerPanelOptionProps | undefined
    >(undefined);

    const prevOption = usePreviousValue(selectedOption);
    const prevFilter = usePreviousValue(selectedFilter);

    const routerParams = useRouterParams({
        method: "push",
        shallow: false,
        scroll: false,
    });

    const checkRuleCompleted = useCallback(() => {
        return currentStep.rules.every((rule) => {
            return rule.selected && rule.filter_rule?.selected;
        });
    }, [currentStep]);

    useEffect(() => {
        setNextStepValid(checkRuleCompleted());
    }, [checkRuleCompleted, setNextStepValid]);

    useEffect(() => {
        setCurrentRuleIndex(
            ruleArray.findIndex(
                (r: IVisualizerPanelRulesProps) => r.title === rule.title,
            ),
        );
        onInvalidOption("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (
            prevFilter &&
            selectedFilter &&
            prevFilter.id !== selectedFilter.id
        ) {
            if (lastSelectedFilter && lastSelectedFilter !== prevFilter) {
                // Reset the invalid message if the user has selected a new filter after it's already displayed
                onInvalidOption("");
            }
            setLastSelectedFilter(prevFilter);
        }
    }, [prevFilter, selectedFilter, onInvalidOption, lastSelectedFilter]);

    useEffect(() => {
        // If there was a valid previous option and the current is undefined...
        if (prevOption && !selectedOption && selectedFilter) {
            // ...set the parent label warning
            onInvalidOption(
                `${prevOption.option} is unavailable in ${selectedFilter.option}. Please update your selection.`,
            );
        }
    }, [prevOption, selectedOption, selectedFilter, onInvalidOption]);

    useEffect(() => {
        if (currentRuleIndex !== -1) {
            const shouldDeselect = ruleArray[currentRuleIndex].options.some(
                (option) => {
                    if (selectedFilter) {
                        const finishPath = getSelectedFinishImagePath(
                            [selectedFilter],
                            option.finish_images,
                        );
                        if (finishPath === null && option === selectedOption) {
                            return true;
                        }
                    }
                },
            );

            if (shouldDeselect) {
                routerParams.clearParams(
                    ruleArray[currentRuleIndex].id.toString(),
                );
            }
        }
    }, [
        selectedFilter,
        ruleArray,
        currentRuleIndex,
        routerParams,
        selectedOption,
    ]);

    if (currentRuleIndex === -1) {
        return null;
    }

    const currentRule = ruleArray[currentRuleIndex];
    const currentOption = currentRule.options.find(
        (o) =>
            o.id.toString() ===
            routerParams.getParamValue(`rule-${currentRule.id}`),
    );
    const currentFilterRule = currentRule.filter_rule;
    const currentFilter = currentFilterRule?.options.find(
        (o) =>
            o.id.toString() ===
            routerParams.getParamValue(`rule-${currentFilterRule.id}`),
    );

    const uniqueOptionTypes = [
        ...new Set(
            currentRule.options
                .map((o) => o.obj_type)
                .sort((a, b) => a.order - b.order)
                .map((o) => o.display_name),
        ),
    ];
    const uniqueOptionThemes = [
        ...new Set(
            currentRule.options
                .map((o) => {
                    if (o.themes) {
                        return o.themes.map((t) => t.name).flat();
                    }

                    return null;
                })
                .flat(),
        ),
    ].sort();

    const needsTabs =
        uniqueOptionTypes
            .map((type: string | null) => {
                return type;
            })
            .flat().length > 1;

    const stepTitleArticle = startsWithVowel(currentStep.title) ? "an" : "a";

    const calculateTrimSelectionUrl = (option: IVisualizerPanelOptionProps) => {
        const newlySelectedOptionId = option.id.toString();

        const query = {
            ...router.query,
        };

        if (!nextStep) {
            // unless we're on the first step, changing something means disabling mirroring
            query["mirror"] = "false";
        }
        const mirror = query["mirror"] !== "false";

        query[`rule-${currentRule.id}`] = newlySelectedOptionId;
        // Re-do mirroring logic on the frontend. This prevents the backend from redirecting which was duplicating a slow api request
        if (mirror && currentRule.linked_rule) {
            query[`rule-${currentRule.linked_rule}`] = newlySelectedOptionId;
        }

        if (
            currentFilter &&
            currentFilterRule &&
            option.finish_images &&
            !(currentFilter.id in option.finish_images)
        ) {
            // If there's a finish selected and the newly selected option doesn't come in that finish, deselect the finish
            delete query[`rule-${currentFilterRule.id}`];

            // Re-do mirroring logic on the frontend. This prevents the backend from redirecting which was duplicating a slow api request
            if (mirror && currentFilterRule.linked_rule) {
                delete query[`rule-${currentFilterRule.linked_rule}`];
            }
        }

        return { query };
    };

    const calculateFinishSelectionUrl = (
        option: IVisualizerPanelOptionProps,
    ) => {
        const newFilterOptionId = option.id.toString();

        const query = {
            ...router.query,
        };

        // This matches the second step currently but tbh I'm not sure the best way to get this
        // Regardless - when on the second step, un-mirror when anything is selected
        if (!nextStep) {
            // unless we're on the first step, changing something means disabling mirroring
            query["mirror"] = "false";
        }
        const mirror = query["mirror"] !== "false";

        if (currentFilterRule) {
            query[`rule-${currentFilterRule.id}`] = newFilterOptionId;

            // Re-do mirroring logic on the frontend. This prevents the backend from redirecting which was duplicating a slow api request
            if (mirror && currentFilterRule.linked_rule) {
                query[`rule-${currentFilterRule.linked_rule}`] =
                    newFilterOptionId;
            }
        }

        if (
            !(
                currentOption?.finish_images &&
                newFilterOptionId in currentOption.finish_images
            )
        ) {
            // Currently selected trim is not available in this finish, deselect that trim
            delete query[`rule-${currentRule.id}`];

            // Re-do mirroring logic on the frontend. This prevents the backend from redirecting which was duplicating a slow api request
            if (mirror && currentRule.linked_rule) {
                delete query[`rule-${currentRule.linked_rule}`];
            }
        }

        return { query };
    };

    const doesCurrentOptionHaveFilter = (filterID: number) => {
        let hasFinish = false;
        if (currentOption && currentOption.finish_images) {
            const finishImageKeys = Object.keys(currentOption?.finish_images);
            const imageKey = finishImageKeys.find(
                (k) => k.toString() === filterID.toString(),
            );

            if (imageKey) hasFinish = true;
        }

        // In the case that there is no current option, everything should be 'available'
        if (!currentOption) {
            hasFinish = true;
        }

        return hasFinish;
    };

    return (
        <>
            <header className={styles.panelHeader}>
                <div>
                    <Button className={styles.panelCloseIcon} slot="close">
                        <XIcon />
                        <VisuallyHidden>Close Panel</VisuallyHidden>
                    </Button>
                    <h5>{`Select ${stepTitleArticle} ${currentStep.title} ${currentRule.title}`}</h5>
                </div>
            </header>
            <section className={styles.finishContainer}>
                <header
                    className={classNames({
                        [styles.filterSelectWarning]: !currentFilter,
                    })}
                >
                    <div>1</div> Choose a Finish:{" "}
                    <span>
                        {currentFilter
                            ? currentFilter.option
                            : "Finish selection needed"}
                    </span>
                </header>
                <section>
                    {currentFilterRule &&
                        currentFilterRule.options.map(
                            (filter: IVisualizerPanelOptionProps, i) => {
                                const url = calculateFinishSelectionUrl(filter);
                                return (
                                    <Tooltip
                                        key={`${currentRule.title}--${currentFilterRule.title}--${i}`}
                                        label={filter.option}
                                    >
                                        <Link href={url} passHref>
                                            <Pressable>
                                                <Clickable
                                                    unstyled
                                                    className={classNames({
                                                        [styles.finishTile]:
                                                            true,
                                                        [styles.finishTileSelected]:
                                                            filter.id.toString() ===
                                                            routerParams.getParamValue(
                                                                `rule-${currentFilterRule.id}`,
                                                            ),
                                                        [styles.finishTileNotAvailable]:
                                                            !doesCurrentOptionHaveFilter(
                                                                filter.id,
                                                            ),
                                                    })}
                                                    onMouseEnter={createPrefetcher(
                                                        url,
                                                    )}
                                                >
                                                    <CloudinaryImage
                                                        type={
                                                            filter.primary_image
                                                                ?.image
                                                                ? "fetch"
                                                                : "upload"
                                                        }
                                                        cldId={
                                                            filter.primary_image
                                                                ?.image
                                                                ? filter
                                                                      .primary_image
                                                                      ?.image
                                                                : undefined
                                                        }
                                                        cldSrcSet={[50]}
                                                        aspectRatio="1:1"
                                                    >
                                                        <img
                                                            alt={`swatch of ${filter.option} finish`}
                                                            sizes="50px"
                                                        />
                                                    </CloudinaryImage>
                                                </Clickable>
                                            </Pressable>
                                        </Link>
                                    </Tooltip>
                                );
                            },
                        )}
                </section>
            </section>
            <section className={styles.selectContainer}>
                <header>
                    <div>2</div> {`Choose a ${currentRule.title}`}:{" "}
                    <span>{currentOption ? currentOption.option : ""}</span>
                </header>
                <section>
                    {invalidOption && (
                        <div className={styles.invalidOptionState}>
                            {invalidOption}{" "}
                        </div>
                    )}
                    {needsTabs ? (
                        <OptionTabs
                            optionTypes={uniqueOptionTypes}
                            onOptionSelect={() => onInvalidOption("")}
                            calculateOptionSelectionUrl={
                                calculateTrimSelectionUrl
                            }
                            currentRule={currentRule}
                            currentFilter={currentFilter}
                            selectedOption={selectedOption}
                        />
                    ) : (
                        uniqueOptionThemes.map((theme, i) => {
                            return (
                                <OptionAccordion
                                    key={`${currentRule.title}--${theme}--${i}`}
                                    theme={theme}
                                    options={currentRule.options}
                                    onOptionSelect={() => onInvalidOption("")}
                                    calculateOptionSelectionUrl={
                                        calculateTrimSelectionUrl
                                    }
                                    currentRuleID={currentRule.id}
                                    currentFilter={currentFilter}
                                />
                            );
                        })
                    )}
                </section>
            </section>
            {selectedFilter && selectedOption && (
                <StepButtons
                    ruleArray={ruleArray}
                    currentRuleIndex={currentRuleIndex}
                    nextStep={nextStep}
                    onNextStep={onNextStep}
                    onNewRuleSelected={(
                        newRule: IVisualizerPanelRulesProps,
                    ) => {
                        setCurrentRuleIndex(
                            ruleArray.findIndex((r) => r === newRule),
                        );
                    }}
                    nextStepValid={nextStepValid}
                />
            )}
        </>
    );
};
